/**
 * PostgreSQL inverse-parse branch for the JSON filter operator — inverts
 * `buildJsonConditionSql`'s PG emit shapes (`common/utils/jsonFilterSql.ts`,
 * NOT modified here). Split out of `predicateJsonBranch.ts` because PG's
 * emit differs structurally from the other 4 engines in two ways that don't
 * fit the shared `extractExprSrc` + optional-AND-guard shape:
 *
 *   1. **Field coercion.** `coerceForDeclaredOnText` ALWAYS wraps the field —
 *      `(col)::jsonb` (native/declared-native) or
 *      `(CASE WHEN col IS JSON THEN (col)::jsonb END)` (declared-on-text) —
 *      there is no bare-field case. `stripPgCoercedField` inverts this.
 *   2. **Path shape.** PG paths are a `{a,b}`-style text-array literal
 *      (`jsonPathToPgArray`), not a `$.a.b` JSONPath string.
 *      `pgArrayLiteralToPath` inverts it (see its header for the round-trip
 *      argument — it need not recover the user's literal original path, only
 *      a path that re-emits to the SAME array literal).
 *
 * `is_valid` / `is_not_valid` use the RAW (uncoerced) field per the emitter,
 * so they bypass `stripPgCoercedField` entirely.
 *
 * Every matcher is conservative: a non-match returns `null`, never a guess.
 */

import type { FilterCondition } from '../../core/types/workspaceTypes';
import { CONTAINS_ESCAPE_SRC, containsValueOfPattern } from './containsLikeSql';
import { QUALIFIED_IDENT_SRC } from './predicateIdentGrammar';
import {
  NUMERIC_VALUE_SRC,
  STRING_VALUE_SRC,
  escapeRegExp,
  parseQuotedStringList,
  unescapeSqlString,
  firstJsonPathMatch,
  JSON_NUMERIC_SUB_OPS,
  type JsonParseResult,
} from './predicateJsonHelpers';

const FIELD_SRC = `(${QUALIFIED_IDENT_SRC})`;

type CoercedStrip = { field: string; declaredOnText: boolean; rest: string };

/** Strip a leading coerced-field expression (`(field)::jsonb` or the
 *  declared-on-text CASE form) from the START of `s`, returning the field
 *  text, whether it was the declared-on-text form, and everything after. */
function stripPgCoercedField(s: string): CoercedStrip | null {
  let m = s.match(
    new RegExp(`^\\(CASE WHEN ${FIELD_SRC} IS JSON THEN \\(${FIELD_SRC}\\)::jsonb END\\)([\\s\\S]*)$`),
  );
  if (m) {
    const [, f1, f2, rest] = m;
    if (f1 !== f2) return null;
    return { field: f1, declaredOnText: true, rest };
  }
  m = s.match(new RegExp(`^\\(${FIELD_SRC}\\)::jsonb([\\s\\S]*)$`));
  if (m) {
    const [, field, rest] = m;
    return { field, declaredOnText: false, rest };
  }
  return null;
}

/**
 * Reverse of `jsonPathToPgArray`. Round-trip-safe by construction (proved in
 * the module doc): a digit-only segment becomes a `[N]` bracket append, any
 * other segment must be a plain identifier (`[A-Za-z_][A-Za-z0-9_]*`) and
 * becomes a `.segment` dot append. Any segment that's neither (backslash
 * escapes, embedded commas/braces/quotes) bails to `null` — the emitter CAN
 * produce such array literals for exotic path segment names, but re-deriving
 * the exact original path from the escaped array form is not attempted here;
 * left as a documented residual (see task report).
 */
export function pgArrayLiteralToPath(lit: string): string | null {
  if (!lit.startsWith('{') || !lit.endsWith('}')) return null;
  const body = lit.slice(1, -1);
  if (body === '') return '$';
  const segments: string[] = [];
  let cur = '';
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (ch === '\\' && i + 1 < body.length) {
      cur += body[i + 1];
      i += 1;
      continue;
    }
    if (ch === ',') {
      segments.push(cur);
      cur = '';
      continue;
    }
    cur += ch;
  }
  segments.push(cur);
  if (segments.some((seg) => seg.length === 0)) return null;
  let path = '$';
  for (const seg of segments) {
    if (/^\d+$/.test(seg)) {
      path += `[${seg}]`;
    } else if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(seg)) {
      path += `.${seg}`;
    } else {
      return null;
    }
  }
  return path;
}

type PgMatch = { field: string; declaredOnText: boolean; path: string; value?: FilterCondition['value'] };

/** Strip the coerced field from the FRONT of `s`, then match a per-op suffix
 *  regex against the remainder. `suffixRe` must capture the PG array-literal
 *  path as its first group. */
function matchCoercedSuffix(
  s: string,
  suffixRe: RegExp,
  extra?: (m: RegExpMatchArray) => FilterCondition['value'] | undefined,
): PgMatch | null {
  const stripped = stripPgCoercedField(s);
  if (!stripped) return null;
  const m = stripped.rest.match(suffixRe);
  if (!m) return null;
  const path = pgArrayLiteralToPath(m[1]);
  if (path === null) return null;
  return { field: stripped.field, declaredOnText: stripped.declaredOnText, path, value: extra?.(m) };
}

function matchPathEq(s: string): PgMatch | null {
  return matchCoercedSuffix(s, new RegExp(`^ #>> '([^']*)' = ${STRING_VALUE_SRC}$`), (m) =>
    unescapeSqlString(m[2]),
  );
}

/**
 * Uncoerced PG extract-and-compare shape: `field #>> 'lit' = 'value'` with NO
 * `::jsonb` cast and no validity guard. Never emitted by the `json` operator
 * (which always routes through `coerceForDeclaredOnText`) — this is
 * EXCLUSIVELY the shape `filterWhereBuilder.ts`'s `case 'lookup'` emits for a
 * loose FK that declares a `sourcePath` into a JSON column:
 * `extractTextSql(engine, quotedField, sourcePath) = escapeAsTextLiteral(value)`
 * → on PG, `pgsqlExtractText` uses whatever field it's given VERBATIM
 * (`${quotedField} #>> '...'`, no cast) — see `pgsqlExtractText` in
 * `jsonFilterSql.ts`; `filterWhereBuilder.ts` passes the bare field.
 *
 * NOT wired into `parsePgJsonPredicate`'s dispatch below: a general
 * "uncoerced extract" branch there would be a round-trip HAZARD — a plain
 * JSON `path_eq` chip always re-emits through `coerceForDeclaredOnText`
 * (ALWAYS adds the cast), so accepting the uncoerced shape as a `path_eq`
 * chip would silently ADD a cast on re-commit (different SQL than what was
 * there before). Exported for the loose-FK caller-side enrichment ONLY
 * (`looseFkEditEnrichment.ts`'s `enrichPgJsonLookupFromRaw`), which only ever
 * constructs a `lookup` condition — whose re-emit path (`filterWhereBuilder.ts`)
 * also skips the cast, keeping the round-trip property intact.
 */
export function matchPgLookupExtract(s: string): { field: string; path: string; value: string } | null {
  const m = s.match(new RegExp(`^${FIELD_SRC} #>> '([^']*)' = ${STRING_VALUE_SRC}$`));
  if (!m) return null;
  const path = pgArrayLiteralToPath(m[2]);
  if (path === null) return null;
  return { field: m[1], path, value: unescapeSqlString(m[3]) };
}

function matchPropertyEnum(s: string): PgMatch | null {
  // Not routed through `matchCoercedSuffix` — a failed `parseQuotedStringList`
  // must veto the WHOLE match (fall back to raw text), which `extra`'s
  // `| undefined` return can't express (an undefined `value` would still
  // produce a — wrong — condition with no value).
  const stripped = stripPgCoercedField(s);
  if (!stripped) return null;
  const m = stripped.rest.match(/^ #>> '([^']*)' IN \((.+)\)$/);
  if (!m) return null;
  const path = pgArrayLiteralToPath(m[1]);
  if (path === null) return null;
  const values = parseQuotedStringList(m[2]);
  if (!values) return null;
  return { field: stripped.field, declaredOnText: stripped.declaredOnText, path, value: values };
}

function matchHasProperty(s: string, negate: boolean): PgMatch | null {
  const suffix = negate ? 'IS NULL' : 'IS NOT NULL';
  return matchCoercedSuffix(s, new RegExp(`^ #> '([^']*)' ${suffix}$`));
}

function matchPathIsNull(s: string, negate: boolean): PgMatch | null {
  const suffix = negate ? 'IS NOT NULL' : 'IS NULL';
  return matchCoercedSuffix(s, new RegExp(`^ #>> '([^']*)' ${suffix}$`));
}

function matchPathContains(s: string): PgMatch | null {
  if (!s.startsWith('LOWER(')) return null;
  const afterLower = s.slice('LOWER('.length);
  const stripped = stripPgCoercedField(afterLower);
  if (!stripped) return null;
  const re = new RegExp(`^ #>> '([^']*)'\\) LIKE LOWER\\(${STRING_VALUE_SRC}\\)${CONTAINS_ESCAPE_SRC}$`);
  const m = stripped.rest.match(re);
  if (!m) return null;
  const path = pgArrayLiteralToPath(m[1]);
  if (path === null) return null;
  // The pattern `containsLikeSql` wrote (or the backslash form saved before).
  const value = containsValueOfPattern(unescapeSqlString(m[2]), m[3], 'pgsql');
  if (value === null) return null;
  return { field: stripped.field, declaredOnText: stripped.declaredOnText, path, value };
}

function matchNumericOp(s: string, op: '>' | '>=' | '<' | '<='): PgMatch | null {
  // `castNumericSql` wraps the WHOLE extract expr in an extra pair of parens:
  // `(<coercedField> #>> '<arr>')::numeric`. Peel that outer `(` before
  // stripping the coerced field.
  if (!s.startsWith('(')) return null;
  const stripped = stripPgCoercedField(s.slice(1));
  if (!stripped) return null;
  const re = new RegExp(`^ #>> '([^']*)'\\)::numeric ${escapeRegExp(op)} ${NUMERIC_VALUE_SRC}$`);
  const m = stripped.rest.match(re);
  if (!m) return null;
  const path = pgArrayLiteralToPath(m[1]);
  if (path === null) return null;
  return { field: stripped.field, declaredOnText: stripped.declaredOnText, path, value: Number(m[2]) };
}

function matchPathNeq(s: string): PgMatch | null {
  if (!s.startsWith('(') || !s.endsWith(')')) return null;
  const inner = s.slice(1, -1);
  const stripped1 = stripPgCoercedField(inner);
  if (!stripped1) return null;
  const m1 = stripped1.rest.match(/^ #>> '([^']*)' IS NULL OR ([\s\S]*)$/);
  if (!m1) return null;
  const [, arrLit, tail] = m1;
  const stripped2 = stripPgCoercedField(tail);
  if (!stripped2) return null;
  if (stripped2.field !== stripped1.field || stripped2.declaredOnText !== stripped1.declaredOnText) return null;
  const m2 = stripped2.rest.match(new RegExp(`^ #>> '([^']*)' <> ${STRING_VALUE_SRC}$`));
  if (!m2) return null;
  if (m2[1] !== arrLit) return null;
  const path = pgArrayLiteralToPath(arrLit);
  if (path === null) return null;
  return {
    field: stripped1.field,
    declaredOnText: stripped1.declaredOnText,
    path,
    value: unescapeSqlString(m2[2]),
  };
}

function matchIsValid(s: string, negate: boolean): { field: string } | null {
  const re = negate ? new RegExp(`^${FIELD_SRC} IS NOT JSON$`) : new RegExp(`^${FIELD_SRC} IS JSON$`);
  const m = s.match(re);
  return m ? { field: m[1] } : null;
}

export function parsePgJsonPredicate(s: string): JsonParseResult | null {
  for (const [sub, negate] of [['is_valid', false], ['is_not_valid', true]] as const) {
    const m = matchIsValid(s, negate);
    if (m) {
      return {
        field: m.field,
        condition: { operator: 'json', jsonSubOperator: sub, jsonDeclaredOnText: true },
      };
    }
  }
  return firstJsonPathMatch([
    ['has_property', () => matchHasProperty(s, false)],
    ['not_has_property', () => matchHasProperty(s, true)],
    ['property_enum', () => matchPropertyEnum(s)],
    ['path_eq', () => matchPathEq(s)],
    ['path_neq', () => matchPathNeq(s)],
    ['path_is_null', () => matchPathIsNull(s, false)],
    ['path_is_not_null', () => matchPathIsNull(s, true)],
    ['path_contains', () => matchPathContains(s)],
    ...JSON_NUMERIC_SUB_OPS.map(([sub, op]) => [sub, () => matchNumericOp(s, op)] as const),
  ]);
}
